import { PayloadAction } from '@reduxjs/toolkit';
import { IRestApiResponse } from 'interfaces';
import {
  all, call, put, select, take, takeLatest,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import DIRECT_CHAT_API from './constants';
import {
  IDirectChatConversation,
  IDirectChatState,
  ISendDirectFormInviteDto,
  ISendDirectMeetingMessageDto,
  ISendDirectMessageDto,
} from './interface';
import {
  selectDirectChatConversationId,
  selectDirectChatConversations,
  selectDirectChatConversationsPage,
  selectDirectChatFilters,
  selectTenantUsersPage,
  selectTenantUsersKeyword,
} from './selectors';
import { directChatActions } from './slice';

const USERS_PAGE_LIMIT = 10;
const CONVERSATIONS_PAGE_LIMIT = 10;

const extractConversationsResult = (data: any) => {
  if (Array.isArray(data?.result)) {
    return data.result;
  }
  return Array.isArray(data) ? data : [];
};

function* getRecentUsersGenerator() {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: DIRECT_CHAT_API.GET_RECENT_USERS.path,
        method: DIRECT_CHAT_API.GET_RECENT_USERS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(directChatActions.getRecentUsersSucceeded(
        Array.isArray(response.data) ? response.data : [],
      ));
    } else {
      yield put(directChatActions.getRecentUsersFailed());
    }
  } catch (error) {
    yield put(directChatActions.getRecentUsersFailed());
  }
}

function* getTenantUsersGenerator(
  action: PayloadAction<{ keyword?: string } | undefined>,
) {
  const keyword = action?.payload?.keyword || '';
  try {
    let queryParams = `page=1&limit=${USERS_PAGE_LIMIT}`;
    if (keyword) {
      queryParams += `&keyword=${encodeURIComponent(keyword)}`;
    }
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${DIRECT_CHAT_API.GET_TENANT_USERS.path}?${queryParams}`,
        method: DIRECT_CHAT_API.GET_TENANT_USERS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(directChatActions.getTenantUsersSucceeded(response.data));
    } else {
      yield put(directChatActions.getTenantUsersFailed());
    }
  } catch (error) {
    yield put(directChatActions.getTenantUsersFailed());
  }
}

function* loadMoreTenantUsersGenerator() {
  const currentPage: number = yield select(selectTenantUsersPage);
  const keyword: string = yield select(selectTenantUsersKeyword);
  const nextPage = currentPage + 1;

  try {
    let queryParams = `page=${nextPage}&limit=${USERS_PAGE_LIMIT}`;
    if (keyword) {
      queryParams += `&keyword=${encodeURIComponent(keyword)}`;
    }
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${DIRECT_CHAT_API.GET_TENANT_USERS.path}?${queryParams}`,
        method: DIRECT_CHAT_API.GET_TENANT_USERS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(directChatActions.appendTenantUsers({
        result: response.data.result,
        total: response.data.total,
        page: nextPage,
      }));
    } else {
      yield put(directChatActions.getTenantUsersFailed());
    }
  } catch (error) {
    yield put(directChatActions.getTenantUsersFailed());
  }
}

function* openConversationGenerator(
  action: PayloadAction<{ userId: string }>,
) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: DIRECT_CHAT_API.POST_OPEN_CONVERSATION.path,
        method: DIRECT_CHAT_API.POST_OPEN_CONVERSATION.method,
      },
      { userId: action.payload.userId },
      true,
    );
    if (response.statusCode === 200 || response.statusCode === 201) {
      yield put(directChatActions.openConversationSucceeded(response.data));
      yield put(directChatActions.getConversations());
    } else {
      yield put(directChatActions.openConversationFailed());
    }
  } catch (error) {
    yield put(directChatActions.openConversationFailed());
  }
}

function* getConversationsGenerator() {
  yield put(directChatActions.setLoading(true));
  const filters: IDirectChatState['filters'] = yield select(selectDirectChatFilters);

  let options = `&page=1&limit=${CONVERSATIONS_PAGE_LIMIT}`;
  if (filters.keyword.length > 0) {
    options = `${options}&keyword=${filters.keyword}`;
  }

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${DIRECT_CHAT_API.GET_CONVERSATIONS.path}?isExpert=true${options}`,
        method: DIRECT_CHAT_API.GET_CONVERSATIONS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      const result = extractConversationsResult(response.data);
      const total = response.data?.total ?? result.length;
      yield put(directChatActions.getConversationsSucceeded({ result, total }));
    } else {
      yield put(directChatActions.getConversationsFailed('Conversations fetching failed'));
    }
  } catch (error) {
    yield put(directChatActions.getConversationsFailed('Conversations fetching failed'));
  }
}

function* loadMoreConversationsGenerator() {
  const currentPage: number = yield select(selectDirectChatConversationsPage);
  const filters: IDirectChatState['filters'] = yield select(selectDirectChatFilters);
  const nextPage = currentPage + 1;

  let options = `&page=${nextPage}&limit=${CONVERSATIONS_PAGE_LIMIT}`;
  if (filters.keyword.length > 0) {
    options = `${options}&keyword=${filters.keyword}`;
  }

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${DIRECT_CHAT_API.GET_CONVERSATIONS.path}?isExpert=true${options}`,
        method: DIRECT_CHAT_API.GET_CONVERSATIONS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      const result = extractConversationsResult(response.data);
      const total = response.data?.total ?? result.length;
      yield put(directChatActions.appendConversations({ result, total, page: nextPage }));
    } else {
      yield put(directChatActions.getConversationsFailed('Conversations fetching failed'));
    }
  } catch (error) {
    yield put(directChatActions.getConversationsFailed('Conversations fetching failed'));
  }
}

function* createConversationGenerator(
  action: PayloadAction<{ userId: string; resolve: any; reject: any }>,
) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: DIRECT_CHAT_API.POST_CREATE_CONVERSATION.path,
        method: DIRECT_CHAT_API.POST_CREATE_CONVERSATION.method,
      },
      { userId: action.payload.userId },
      true,
    );
    if (response.statusCode === 200 || response.statusCode === 201) {
      yield put(directChatActions.createConversationSucceeded(response.data));
      yield put(directChatActions.getConversations());
      yield action.payload.resolve(response.data);
    } else {
      yield put(directChatActions.createConversationFailed());
      yield action.payload.reject();
    }
  } catch (error) {
    yield put(directChatActions.createConversationFailed());
    yield action.payload.reject();
  }
}

function* redirectToFirstConversation() {
  // Refresh the conversations list and wait for it to complete
  yield put(directChatActions.getConversations());
  yield take([
    directChatActions.getConversationsSucceeded.type,
    directChatActions.getConversationsFailed.type,
  ]);

  const conversations: IDirectChatConversation[] = yield select(selectDirectChatConversations);
  if (conversations.length > 0) {
    const firstId = conversations[0]._id;
    yield put(directChatActions.setConversationId(firstId));
    window.location.replace(`/sessions/messages/direct/${firstId}`);
  } else {
    yield put(directChatActions.setConversationId(null));
    window.location.replace('/sessions/messages/direct/');
  }
}

function* getSingleConversationGenerator() {
  const conversationId: string = yield select(selectDirectChatConversationId);
  if (!conversationId) {
    yield put(directChatActions.getSingleConversationFailed('empty conversation id'));
    return;
  }

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${DIRECT_CHAT_API.GET_SINGLE_CONVERSATION.path}/${conversationId}?isExpert=true`,
        method: DIRECT_CHAT_API.GET_SINGLE_CONVERSATION.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(directChatActions.getSingleConversationSucceeded(response.data));
    } else {
      yield put(directChatActions.getSingleConversationFailed('Failed to fetch conversation'));
      yield call(redirectToFirstConversation);
    }
  } catch (error) {
    yield put(directChatActions.getSingleConversationFailed('Failed to fetch conversation'));
    yield call(redirectToFirstConversation);
  }
}

function* sendMessageGenerator(action: PayloadAction<ISendDirectMessageDto>) {
  const conversationId: string = yield select(selectDirectChatConversationId);

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${DIRECT_CHAT_API.PUT_SEND_MESSAGE.path}/${conversationId}/send?isExpert=true`,
        method: DIRECT_CHAT_API.PUT_SEND_MESSAGE.method,
      },
      {
        body: action.payload.body,
        attachments: action.payload.attachments,
      },
      true,
    );
    if (response.statusCode === 200) {
      yield put(directChatActions.sendMessageSucceeded());
      yield put(directChatActions.getConversations());
      yield action.payload.resolve();
    } else {
      yield put(directChatActions.sendMessageFailed('Failed to send message'));
      yield action.payload.reject();
    }
  } catch (error) {
    yield put(directChatActions.sendMessageFailed('Failed to send message'));
    yield action.payload.reject();
  }
}

function* sendMeetingMessageGenerator(action: PayloadAction<ISendDirectMeetingMessageDto>) {
  const conversationId: string = yield select(selectDirectChatConversationId);

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${DIRECT_CHAT_API.POST_SEND_MEETING_MESSAGE.path}/${conversationId}/send-meeting?isExpert=true`,
        method: DIRECT_CHAT_API.POST_SEND_MEETING_MESSAGE.method,
      },
      {},
      true,
    );
    if (response.statusCode === 200) {
      yield put(directChatActions.sendMeetingMessageSucceeded());
      yield action.payload.resolve();
    } else {
      yield put(directChatActions.sendMeetingMessageFailed('Failed to create instant meeting'));
      yield action.payload.reject();
    }
  } catch (error) {
    yield put(directChatActions.sendMeetingMessageFailed('Failed to create instant meeting'));
    yield action.payload.reject();
  }
}

function* sendFormInviteGenerator(action: PayloadAction<ISendDirectFormInviteDto>) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: DIRECT_CHAT_API.POST_SEND_FORM_INVITE.path,
        method: DIRECT_CHAT_API.POST_SEND_FORM_INVITE.method,
      },
      action.payload,
      true,
    );
    if (response.statusCode === 201) {
      yield put(directChatActions.sendFormInviteSucceeded());
      yield put(directChatActions.getConversations());
    } else {
      yield put(directChatActions.sendFormInviteFailed('Failed to send the form invite'));
    }
  } catch (error) {
    yield put(directChatActions.sendFormInviteFailed('Failed to send the form invite'));
  }
}

function* readAllMessagesGenerator() {
  const conversationId: string = yield select(selectDirectChatConversationId);
  if (!conversationId) return;

  try {
    yield call(
      request,
      {
        path: `${DIRECT_CHAT_API.POST_READ_ALL.path}/${conversationId}/read-all?isExpert=true`,
        method: DIRECT_CHAT_API.POST_READ_ALL.method,
      },
      {},
      true,
    );
  } catch (err) {
    // Silently fail for read-all messages
  }
}

export function* directChatSaga() {
  yield all([
    takeLatest(directChatActions.getRecentUsers.type, getRecentUsersGenerator),
    takeLatest(directChatActions.getTenantUsers.type, getTenantUsersGenerator),
    takeLatest(directChatActions.loadMoreTenantUsers.type, loadMoreTenantUsersGenerator),
    takeLatest(directChatActions.openConversation.type, openConversationGenerator),
    takeLatest(directChatActions.getConversations.type, getConversationsGenerator),
    takeLatest(directChatActions.createConversation.type, createConversationGenerator),
    takeLatest(directChatActions.getSingleConversation.type, getSingleConversationGenerator),
    takeLatest(directChatActions.setConversationId.type, getSingleConversationGenerator),
    takeLatest(directChatActions.setKeyword.type, getConversationsGenerator),
    takeLatest(directChatActions.sendMessage.type, sendMessageGenerator),
    takeLatest(directChatActions.sendMeetingMessage.type, sendMeetingMessageGenerator),
    takeLatest(directChatActions.sendFormInvite.type, sendFormInviteGenerator),
    takeLatest(directChatActions.readAllMessages.type, readAllMessagesGenerator),
    takeLatest(directChatActions.loadMoreConversations.type, loadMoreConversationsGenerator),
  ]);
}

export default directChatSaga;
